/* eslint-disable max-lines */
export interface LayoutBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface AutoLayout {
  boxes: LayoutBox[];
  totalHeight: number;
}

const MIN_ASPECT = 0.2;
const MAX_ASPECT = 5;
export const DEFAULT_ASPECT = 16 / 9;

interface RowFit {
  aspects: number[];
  height: number;
}

function clampAspects(aspects: number[]): number[] {
  return aspects.map((a) =>
    a && isFinite(a) && a > 0
      ? Math.min(Math.max(a, MIN_ASPECT), MAX_ASPECT)
      : DEFAULT_ASPECT
  );
}

function balancedRowSizes(n: number, r: number): number[] {
  const base = Math.floor(n / r);
  const extra = n % r;
  return Array.from({ length: r }, (_, i) => base + (i < extra ? 1 : 0));
}

function chunk<T>(items: T[], size: number): T[][] {
  const groups: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    groups.push(items.slice(i, i + size));
  }
  return groups;
}

function fitRow(
  rowAspects: number[],
  width: number,
  gap: number,
  capHeight: number
): RowFit {
  const sumAspect = rowAspects.reduce((sum, a) => sum + a, 0);
  const naturalWidth = capHeight * sumAspect + gap * (rowAspects.length - 1);
  const height =
    naturalWidth > width
      ? (width - gap * (rowAspects.length - 1)) / sumAspect
      : capHeight;
  return { aspects: rowAspects, height };
}

function buildLayout(
  rows: RowFit[],
  width: number,
  height: number,
  gap: number
): AutoLayout {
  const count = rows.reduce((sum, row) => sum + row.aspects.length, 0);
  const usedHeight =
    rows.reduce((sum, row) => sum + row.height, 0) + gap * (rows.length - 1);

  const boxes: LayoutBox[] = new Array(count);
  let top = usedHeight > height ? 0 : Math.max(0, (height - usedHeight) / 2);
  let i = 0;
  rows.forEach((row) => {
    const rowWidth =
      row.aspects.reduce((sum, a) => sum + row.height * a, 0) +
      gap * (row.aspects.length - 1);
    let left = Math.max(0, (width - rowWidth) / 2);
    row.aspects.forEach((a) => {
      const itemWidth = row.height * a;
      boxes[i] = { left, top, width: itemWidth, height: row.height };
      left += itemWidth + gap;
      i += 1;
    });
    top += row.height + gap;
  });

  return { boxes, totalHeight: usedHeight };
}

export function computeAutoLayout(
  aspects: number[],
  width: number,
  height: number,
  gap: number
): AutoLayout {
  const count = aspects.length;
  if (count === 0 || width <= 0 || height <= 0) {
    return { boxes: [], totalHeight: 0 };
  }
  const safeAspects = clampAspects(aspects);

  let best: RowFit[] = [];
  let bestScore = -1;
  for (let rowCount = 1; rowCount <= count; rowCount++) {
    const capHeight = (height - gap * (rowCount - 1)) / rowCount;
    if (capHeight <= 0) continue;
    const sizes = balancedRowSizes(count, rowCount);
    const rows: RowFit[] = [];
    let index = 0;
    let score = 0;
    sizes.forEach((size) => {
      const row = fitRow(
        safeAspects.slice(index, index + size),
        width,
        gap,
        capHeight
      );
      index += size;
      rows.push(row);
      row.aspects.forEach((a) => {
        score += row.height * a * row.height;
      });
    });
    if (score > bestScore) {
      bestScore = score;
      best = rows;
    }
  }

  return buildLayout(best, width, height, gap);
}

export function computeFixedLayout(
  aspects: number[],
  width: number,
  height: number,
  gap: number,
  cols: number,
  rows: number
): AutoLayout {
  const count = aspects.length;
  if (count === 0 || width <= 0 || height <= 0) {
    return { boxes: [], totalHeight: 0 };
  }
  const safeAspects = clampAspects(aspects);
  const perRow = Math.max(1, cols);
  const rowCount = Math.max(1, rows);
  const capHeight = Math.max((height - gap * (rowCount - 1)) / rowCount, 1);
  const rowFits = chunk(safeAspects, perRow).map((group) =>
    fitRow(group, width, gap, capHeight)
  );
  return buildLayout(rowFits, width, height, gap);
}
